import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { orgQuery } from "@/lib/org";
import type { LinePattern } from "@/lib/apartmentLines";

export interface ApartmentPatternEntry {
  pattern: LinePattern;
  source: "verified" | "inferred" | "default";
  verifiedBy?: string;
  note?: string;
}

/**
 * apartments 컬렉션 전체 구독 — 단지 ID → 패턴 맵 반환.
 * verified > inferred 우선순위, 없으면 default(1-2|3-4) 적용.
 */
export function useApartments(): Map<string, ApartmentPatternEntry> {
  const [map, setMap] = useState<Map<string, ApartmentPatternEntry>>(new Map());

  useEffect(() => {
    const unsub = onSnapshot(orgQuery("apartments"), (snap) => {
      const m = new Map<string, ApartmentPatternEntry>();
      snap.docs.forEach((d) => {
        const data: any = d.data();
        if (data.verified?.pattern) {
          m.set(d.id, {
            pattern: data.verified.pattern,
            source: "verified",
            verifiedBy: data.verified.verifiedBy,
            note: data.verified.note,
          });
        } else if (data.inferred?.pattern) {
          m.set(d.id, { pattern: data.inferred.pattern, source: "inferred" });
        }
      });
      setMap(m);
    });
    return () => unsub();
  }, []);

  return map;
}

/**
 * 단일 단지 패턴 구독.
 */
export function useApartmentPattern(aptId: string | null): ApartmentPatternEntry | null {
  const [entry, setEntry] = useState<ApartmentPatternEntry | null>(null);

  useEffect(() => {
    if (!aptId) { setEntry(null); return; }
    const unsub = onSnapshot(doc(db, "apartments", aptId), (snap) => {
      if (!snap.exists()) { setEntry(null); return; }
      const d: any = snap.data();
      if (d.verified?.pattern) {
        setEntry({ pattern: d.verified.pattern, source: "verified", verifiedBy: d.verified.verifiedBy, note: d.verified.note });
      } else if (d.inferred?.pattern) {
        setEntry({ pattern: d.inferred.pattern, source: "inferred" });
      } else setEntry(null);
    });
    return () => unsub();
  }, [aptId]);

  return entry;
}
